import { cur } from '@/lib/currency';
import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, ChevronLeft, ChevronRight, Monitor, Users, Calculator,
  BarChart3, Bell, FileText, Building2, AlertTriangle, Receipt,
  TrendingUp, Package, DollarSign, ArrowUpRight, ArrowDownRight,
  Clock, Check, ShoppingCart, Calendar, Briefcase, PieChart,
  Activity, Eye, Download, Plus, Search, Filter,
  Share2, Copy, Mail, MessageCircle, Link2
} from 'lucide-react';

const heading = { fontFamily: "'Outfit', sans-serif" };

/* ─── Mock Sidebar ─── */
function MockSidebar({ activeStep }) {
  const items = [
    { icon: BarChart3, label: 'Dashboard' },
    { icon: Monitor, label: 'POS' },
    { icon: Users, label: 'HR & Payroll' },
    { icon: Calculator, label: 'Accounting' },
    { icon: TrendingUp, label: 'Analytics' },
    { icon: Bell, label: 'Notifications' },
    { icon: FileText, label: 'Documents' },
    { icon: Building2, label: 'Branches' },
    { icon: AlertTriangle, label: 'Alerts' },
    { icon: Receipt, label: 'Expenses' },
    { icon: DollarSign, label: 'Profit & Loss' },
  ];
  return (
    <div className="w-44 bg-[#1D1D1F] rounded-l-xl flex flex-col py-4 shrink-0">
      <div className="px-4 mb-4 flex items-center gap-2">
        <div className="w-6 h-6 rounded-md bg-gradient-to-br from-orange-500 to-amber-500" />
        <span className="text-[11px] font-bold text-white tracking-wider">SMARTO</span>
      </div>
      <div className="flex-1 space-y-0.5 px-2 overflow-hidden">
        {items.map((item, i) => (
          <div key={item.label}
            className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-[10px] font-medium transition-all ${
              i === activeStep
                ? 'bg-white/15 text-white'
                : 'text-white/40 hover:text-white/60'
            }`}>
            <item.icon className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Mock Top Bar ─── */
function MockTopBar({ title }) {
  return (
    <div className="h-10 border-b border-gray-100 flex items-center justify-between px-5">
      <h3 className="text-[13px] font-semibold text-[#1D1D1F]" style={heading}>{title}</h3>
      <div className="flex items-center gap-2">
        <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center">
          <Search className="w-3 h-3 text-gray-400" />
        </div>
        <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center">
          <Bell className="w-3 h-3 text-gray-400" />
        </div>
        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-orange-400 to-amber-400 flex items-center justify-center">
          <span className="text-[8px] font-bold text-white">SA</span>
        </div>
      </div>
    </div>
  );
}

/* ─── KPI Card ─── */
function KpiCard({ label, value, change, positive, icon: Icon, color, delay = 0, inView = true }) {
  return (
    <motion.div
      className="bg-white rounded-lg border border-gray-100 p-3 shadow-sm"
      initial={{ opacity: 0, y: 15 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay, duration: 0.4 }}>
      <div className="flex items-center justify-between mb-2">
        <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${color} flex items-center justify-center`}>
          <Icon className="w-3.5 h-3.5 text-white" />
        </div>
        {change && (
          <span className={`text-[9px] font-bold flex items-center gap-0.5 ${positive ? 'text-success' : 'text-error'}`}>
            {positive ? <ArrowUpRight className="w-2.5 h-2.5" /> : <ArrowDownRight className="w-2.5 h-2.5" />}
            {change}
          </span>
        )}
      </div>
      <p className="text-[15px] font-bold text-[#1D1D1F]" style={heading}>{value}</p>
      <p className="text-[9px] text-[#86868B] mt-0.5">{label}</p>
    </motion.div>
  );
}

/* ─── Table Row ─── */
function TableRow({ cells, delay = 0, highlight = false }) {
  return (
    <motion.div
      className={`grid gap-2 py-1.5 px-3 text-[9px] border-b border-gray-50 ${highlight ? 'bg-orange-50/50' : ''}`}
      style={{ gridTemplateColumns: `repeat(${cells.length}, 1fr)` }}
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay, duration: 0.3 }}>
      {cells.map((c, i) => (
        <span key={i} className={i === 0 ? 'font-medium text-[#1D1D1F]' : 'text-[#86868B]'}>{c}</span>
      ))}
    </motion.div>
  );
}

/* ═══════════════════════════════════════════
   TOUR STEP SCREENS
   ═══════════════════════════════════════════ */

function DashboardScreen() {
  return (
    <div className="p-4 space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        <KpiCard label="Today's Revenue" value="SR 12,450" change="+18.5%" positive icon={DollarSign} color="from-orange-500 to-amber-500" delay={0.1} />
        <KpiCard label="Orders" value="84" change="+12%" positive icon={ShoppingCart} color="from-blue-500 to-indigo-500" delay={0.2} />
        <KpiCard label="Employees" value="47" change="+2" positive icon={Users} color="from-emerald-500 to-teal-500" delay={0.3} />
        <KpiCard label="Low Stock Items" value="3" change="Alert" positive={false} icon={Package} color="from-red-500 to-rose-500" delay={0.4} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
        <div className="col-span-2 bg-white rounded-lg border border-gray-100 p-3">
          <p className="text-[10px] font-semibold text-[#1D1D1F] mb-2">Weekly Revenue</p>
          <div className="flex items-end gap-2 h-20">
            {[45, 72, 58, 85, 65, 92, 78].map((h, i) => (
              <motion.div key={i} className="flex-1 bg-gradient-to-t from-orange-500 to-amber-400 rounded-sm"
                initial={{ height: 0 }} animate={{ height: `${h}%` }}
                transition={{ duration: 0.5, delay: 0.5 + i * 0.08 }} />
            ))}
          </div>
          <div className="flex justify-between mt-1">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
              <span key={d} className="text-[7px] text-[#86868B] flex-1 text-center">{d}</span>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-100 p-3">
          <p className="text-[10px] font-semibold text-[#1D1D1F] mb-2">Top Products</p>
          {['Espresso', 'Croissant', 'Latte', 'Sandwich'].map((p, i) => (
            <motion.div key={p} className="flex items-center justify-between py-1"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 + i * 0.1 }}>
              <span className="text-[9px] text-[#1D1D1F]">{p}</span>
              <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <motion.div className="h-full bg-orange-400 rounded-full"
                  initial={{ width: 0 }} animate={{ width: `${90 - i * 15}%` }}
                  transition={{ duration: 0.5, delay: 0.8 + i * 0.1 }} />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

function POSScreen() {
  const items = [
    { name: 'Cappuccino', price: '15.00', qty: 2 },
    { name: 'Club Sandwich', price: '28.00', qty: 1 },
    { name: 'Caesar Salad', price: '22.00', qty: 1 },
    { name: 'Fresh Juice', price: '12.00', qty: 3 },
  ];
  const total = items.reduce((s, it) => s + parseFloat(it.price) * it.qty, 0);
  return (
    <div className="p-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 h-full">
      <div className="col-span-3 space-y-2">
        <div className="flex items-center gap-2 mb-2">
          <div className="flex-1 h-7 bg-gray-50 rounded-md flex items-center px-2 gap-1.5">
            <Search className="w-3 h-3 text-gray-300" />
            <span className="text-[9px] text-gray-300">Search products...</span>
          </div>
          <div className="h-7 px-2 bg-gray-50 rounded-md flex items-center gap-1">
            <Filter className="w-3 h-3 text-gray-400" />
            <span className="text-[9px] text-gray-400">All</span>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          {['Hot Drinks', 'Cold Drinks', 'Food', 'Desserts', 'Snacks', 'Combos', 'Specials', 'Add-ons'].map((cat, i) => (
            <motion.div key={cat}
              className={`rounded-lg p-2 text-center text-[9px] font-medium border cursor-default ${
                i === 0 ? 'bg-orange-50 border-orange-200 text-orange-700' : 'bg-white border-gray-100 text-[#86868B]'
              }`}
              initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 + i * 0.05 }}>
              {cat}
            </motion.div>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mt-1">
          {['Espresso', 'Americano', 'Cappuccino', 'Latte', 'Mocha', 'Hot Choco'].map((item, i) => (
            <motion.div key={item}
              className="bg-white border border-gray-100 rounded-lg p-2.5 text-center hover:border-orange-200 transition-colors"
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 + i * 0.06 }}>
              <div className="w-8 h-8 mx-auto rounded-lg bg-gradient-to-br from-amber-100 to-orange-100 mb-1.5" />
              <p className="text-[9px] font-medium text-[#1D1D1F]">{item}</p>
              <p className="text-[8px] text-orange-500 font-semibold">{cur()} 15.00</p>
            </motion.div>
          ))}
        </div>
      </div>
      <div className="col-span-2 bg-white rounded-lg border border-gray-100 p-3 flex flex-col">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] font-semibold text-[#1D1D1F]">Current Order</span>
          <span className="text-[8px] bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded font-medium">#1247</span>
        </div>
        <div className="flex-1 space-y-1 overflow-hidden">
          {items.map((item, i) => (
            <motion.div key={item.name}
              className="flex items-center justify-between py-1.5 px-2 bg-gray-50 rounded-md"
              initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 + i * 0.12 }}>
              <div>
                <p className="text-[9px] font-medium text-[#1D1D1F]">{item.name}</p>
                <p className="text-[8px] text-[#86868B]">x{item.qty}</p>
              </div>
              <span className="text-[9px] font-semibold text-[#1D1D1F]">{cur()} {(parseFloat(item.price) * item.qty).toFixed(2)}</span>
            </motion.div>
          ))}
        </div>
        <div className="border-t border-gray-100 pt-2 mt-2 space-y-1">
          <div className="flex justify-between text-[9px] text-[#86868B]"><span>Subtotal</span><span>{cur()} {total.toFixed(2)}</span></div>
          <div className="flex justify-between text-[9px] text-[#86868B]"><span>VAT (15%)</span><span>{cur()} {(total * 0.15).toFixed(2)}</span></div>
          <div className="flex justify-between text-[11px] font-bold text-[#1D1D1F]"><span>Total</span><span>{cur()} {(total * 1.15).toFixed(2)}</span></div>
        </div>
        <motion.button
          className="mt-2 w-full py-2 bg-gradient-to-r from-orange-500 to-amber-500 rounded-lg text-[10px] font-bold text-white"
          initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.8, type: 'spring' }}>
          Charge {cur()} {(total * 1.15).toFixed(2)}
        </motion.button>
      </div>
    </div>
  );
}

function HRScreen() {
  const employees = [
    { name: 'Ahmed Al-Rashid', role: 'Manager', dept: 'Operations', status: 'Active', salary: '8,500' },
    { name: 'Sara Mohammed', role: 'Accountant', dept: 'Finance', status: 'Active', salary: '6,200' },
    { name: 'Omar Hassan', role: 'Chef', dept: 'Kitchen', status: 'On Leave', salary: '5,800' },
    { name: 'Fatima Ali', role: 'Cashier', dept: 'Sales', status: 'Active', salary: '4,500' },
    { name: 'Khalid Ibrahim', role: 'Driver', dept: 'Logistics', status: 'Active', salary: '3,800' },
  ];
  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-semibold text-[#1D1D1F]">47 Employees</span>
          <span className="text-[8px] bg-success/10 text-success px-1.5 py-0.5 rounded-full font-medium">44 Active</span>
          <span className="text-[8px] bg-warning/10 text-warning px-1.5 py-0.5 rounded-full font-medium">3 On Leave</span>
        </div>
        <motion.div className="flex items-center gap-1.5 px-2.5 py-1 bg-[#1D1D1F] rounded-md"
          initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.3, type: 'spring' }}>
          <Plus className="w-3 h-3 text-white" />
          <span className="text-[9px] font-medium text-white">Add Employee</span>
        </motion.div>
      </div>
      <div className="bg-white rounded-lg border border-gray-100 overflow-hidden">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 py-2 px-3 bg-gray-50 text-[8px] font-bold text-[#86868B] uppercase tracking-wider">
          {['Employee', 'Role', 'Department', 'Status', 'Salary (SR)', 'Actions'].map(h => (
            <span key={h}>{h}</span>
          ))}
        </div>
        {employees.map((emp, i) => (
          <motion.div key={emp.name}
            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 py-2 px-3 text-[9px] border-b border-gray-50 hover:bg-gray-50/50"
            initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 + i * 0.08 }}>
            <div className="flex items-center gap-1.5">
              <div className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-400 to-indigo-400 flex items-center justify-center">
                <span className="text-[7px] font-bold text-white">{emp.name.split(' ').map(n => n[0]).join('')}</span>
              </div>
              <span className="font-medium text-[#1D1D1F]">{emp.name}</span>
            </div>
            <span className="text-[#86868B]">{emp.role}</span>
            <span className="text-[#86868B]">{emp.dept}</span>
            <span className={`text-[8px] font-medium px-1.5 py-0.5 rounded-full w-fit ${
              emp.status === 'Active' ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'
            }`}>{emp.status}</span>
            <span className="text-[#1D1D1F] font-medium">{emp.salary}</span>
            <div className="flex items-center gap-1">
              <Eye className="w-3 h-3 text-gray-400" />
              <FileText className="w-3 h-3 text-gray-400" />
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function AccountingScreen() {
  return (
    <div className="p-4 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
        <KpiCard label="Total Revenue" value="SR 245,800" change="+22%" positive icon={TrendingUp} color="from-emerald-500 to-teal-500" delay={0.1} />
        <KpiCard label="Total Expenses" value="SR 142,300" change="+8%" positive={false} icon={Receipt} color="from-red-500 to-rose-500" delay={0.2} />
        <KpiCard label="Net Profit" value="SR 103,500" change="+34%" positive icon={DollarSign} color="from-blue-500 to-indigo-500" delay={0.3} />
      </div>
      <div className="bg-white rounded-lg border border-gray-100 p-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] font-semibold text-[#1D1D1F]">Recent Journal Entries</span>
          <span className="text-[8px] text-orange-500 font-medium cursor-default">View All</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 py-1.5 px-2 bg-gray-50 rounded-md text-[8px] font-bold text-[#86868B] uppercase tracking-wider mb-1">
          {['Date', 'Description', 'Account', 'Debit', 'Credit'].map(h => <span key={h}>{h}</span>)}
        </div>
        {[
          ['Feb 15', 'Sales Revenue', 'Cash', 'SR 4,200', '-'],
          ['Feb 15', 'Supplier Payment', 'Bank', '-', 'SR 2,800'],
          ['Feb 14', 'Payroll', 'Salary Expense', 'SR 18,500', '-'],
          ['Feb 14', 'Rent Payment', 'Bank', '-', 'SR 5,000'],
        ].map((row, i) => (
          <TableRow key={i} cells={row} delay={0.4 + i * 0.08} />
        ))}
      </div>
    </div>
  );
}

function AnalyticsScreen() {
  return (
    <div className="p-4 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white rounded-lg border border-gray-100 p-3">
          <p className="text-[10px] font-semibold text-[#1D1D1F] mb-2">Revenue Trend</p>
          <svg viewBox="0 0 200 60" className="w-full h-16">
            <motion.path d="M 0 50 C 20 45, 40 30, 60 35 C 80 40, 100 15, 120 20 C 140 25, 160 10, 180 12 L 200 8"
              fill="none" stroke="url(#revGrad)" strokeWidth="2" strokeLinecap="round"
              initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
              transition={{ duration: 1.5, delay: 0.3 }} />
            <motion.path d="M 0 50 C 20 45, 40 30, 60 35 C 80 40, 100 15, 120 20 C 140 25, 160 10, 180 12 L 200 8 L 200 60 L 0 60 Z"
              fill="url(#revAreaGrad)" initial={{ opacity: 0 }} animate={{ opacity: 0.2 }}
              transition={{ delay: 1, duration: 0.5 }} />
            <defs>
              <linearGradient id="revGrad" x1="0" y1="0" x2="200" y2="0" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor="#f97316" /><stop offset="100%" stopColor="#eab308" />
              </linearGradient>
              <linearGradient id="revAreaGrad" x1="0" y1="0" x2="0" y2="60" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor="#f97316" /><stop offset="100%" stopColor="#f97316" stopOpacity="0" />
              </linearGradient>
            </defs>
          </svg>
        </div>
        <div className="bg-white rounded-lg border border-gray-100 p-3">
          <p className="text-[10px] font-semibold text-[#1D1D1F] mb-2">Sales by Category</p>
          <div className="space-y-1.5">
            {[
              { label: 'Hot Drinks', pct: 35, color: 'from-orange-400 to-amber-400' },
              { label: 'Food', pct: 28, color: 'from-blue-400 to-indigo-400' },
              { label: 'Cold Drinks', pct: 22, color: 'from-emerald-400 to-teal-400' },
              { label: 'Desserts', pct: 15, color: 'from-violet-400 to-purple-400' },
            ].map((c, i) => (
              <div key={c.label}>
                <div className="flex justify-between text-[8px] mb-0.5">
                  <span className="text-[#86868B]">{c.label}</span>
                  <span className="font-medium text-[#1D1D1F]">{c.pct}%</span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <motion.div className={`h-full rounded-full bg-gradient-to-r ${c.color}`}
                    initial={{ width: 0 }} animate={{ width: `${c.pct}%` }}
                    transition={{ duration: 0.6, delay: 0.4 + i * 0.1 }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="bg-white rounded-lg border border-gray-100 p-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] font-semibold text-[#1D1D1F]">AI Sales Forecast</span>
          <motion.span className="text-[8px] bg-success/10 text-success px-2 py-0.5 rounded-full font-medium flex items-center gap-1"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}>
            <Activity className="w-2.5 h-2.5" /> Powered by AI
          </motion.span>
        </div>
        <div className="flex items-center gap-3">
          <motion.div className="text-center" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}>
            <p className="text-[18px] font-bold text-[#1D1D1F]" style={heading}>{cur()} 52,400</p>
            <p className="text-[8px] text-[#86868B]">Predicted next week</p>
          </motion.div>
          <motion.div className="flex-1 h-8 bg-gradient-to-r from-emerald-50 to-blue-50 rounded-lg flex items-center px-3"
            initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 0.8, duration: 0.5 }}>
            <span className="text-[8px] text-success font-medium">High confidence (92%)</span>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

function NotificationsScreen() {
  const notifs = [
    { icon: Users, title: 'Leave Request', desc: 'Omar Hassan requested 3 days annual leave', time: '5 min ago', color: 'bg-info', unread: true },
    { icon: Package, title: 'Low Stock Alert', desc: 'Coffee beans below minimum (2 kg remaining)', time: '12 min ago', color: 'bg-warning', unread: true },
    { icon: DollarSign, title: 'Payment Received', desc: 'SR 4,200 received from Branch 2', time: '1 hour ago', color: 'bg-success', unread: true },
    { icon: AlertTriangle, title: 'System Alert', desc: 'Payroll deadline approaching in 3 days', time: '2 hours ago', color: 'bg-error', unread: false },
    { icon: Calendar, title: 'Schedule Update', desc: 'Shift schedule for next week published', time: '3 hours ago', color: 'bg-violet-500', unread: false },
    { icon: TrendingUp, title: 'Revenue Milestone', desc: 'Monthly target reached: 120% achieved', time: '5 hours ago', color: 'bg-orange-500', unread: false },
  ];
  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-semibold text-[#1D1D1F]">All Notifications</span>
          <motion.span className="text-[8px] bg-error text-white px-1.5 py-0.5 rounded-full font-bold"
            initial={{ scale: 0 }} animate={{ scale: [0, 1.3, 1] }} transition={{ delay: 0.3 }}>3 New</motion.span>
        </div>
        <span className="text-[8px] text-orange-500 font-medium cursor-default">Mark all read</span>
      </div>
      <div className="space-y-1.5">
        {notifs.map((n, i) => (
          <motion.div key={i}
            className={`flex items-start gap-2.5 p-2.5 rounded-lg border transition-colors ${
              n.unread ? 'bg-orange-50/40 border-orange-100/50' : 'bg-white border-gray-100'
            }`}
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 + i * 0.08 }}>
            <div className={`w-7 h-7 rounded-lg ${n.color} flex items-center justify-center shrink-0`}>
              <n.icon className="w-3.5 h-3.5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] font-semibold text-[#1D1D1F]">{n.title}</span>
                {n.unread && <div className="w-1.5 h-1.5 rounded-full bg-orange-500" />}
              </div>
              <p className="text-[9px] text-[#86868B] mt-0.5">{n.desc}</p>
              <p className="text-[8px] text-[#86868B]/60 mt-0.5">{n.time}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function DocumentsScreen() {
  const docs = [
    { name: 'Employment Contract - Ahmed', type: 'PDF', size: '245 KB', date: 'Feb 14', icon: '📄' },
    { name: 'Tax Certificate 2025', type: 'PDF', size: '128 KB', date: 'Feb 10', icon: '📋' },
    { name: 'Company License', type: 'PDF', size: '1.2 MB', date: 'Jan 28', icon: '📜' },
    { name: 'Invoice Template', type: 'DOCX', size: '89 KB', date: 'Jan 20', icon: '📑' },
    { name: 'Health Insurance Policy', type: 'PDF', size: '340 KB', date: 'Jan 15', icon: '📄' },
  ];
  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold text-[#1D1D1F]">Company Documents</span>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 px-2 py-1 bg-gray-50 rounded-md">
            <Search className="w-3 h-3 text-gray-400" />
            <span className="text-[9px] text-gray-300">Search...</span>
          </div>
          <motion.div className="flex items-center gap-1 px-2.5 py-1 bg-[#1D1D1F] rounded-md"
            initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.3, type: 'spring' }}>
            <Plus className="w-3 h-3 text-white" />
            <span className="text-[9px] font-medium text-white">Upload</span>
          </motion.div>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mb-2">
        {[
          { label: 'Total Documents', val: '24', color: 'from-blue-500 to-indigo-500' },
          { label: 'This Month', val: '5', color: 'from-emerald-500 to-teal-500' },
          { label: 'Storage Used', val: '12.4 MB', color: 'from-violet-500 to-purple-500' },
        ].map((s, i) => (
          <motion.div key={s.label} className="bg-white rounded-lg border border-gray-100 p-2.5"
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 + i * 0.1 }}>
            <p className="text-[14px] font-bold text-[#1D1D1F]" style={heading}>{s.val}</p>
            <p className="text-[8px] text-[#86868B]">{s.label}</p>
          </motion.div>
        ))}
      </div>
      <div className="bg-white rounded-lg border border-gray-100 overflow-hidden">
        {docs.map((doc, i) => (
          <motion.div key={doc.name}
            className="flex items-center gap-3 p-2.5 border-b border-gray-50 hover:bg-gray-50/50"
            initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 + i * 0.08 }}>
            <span className="text-base">{doc.icon}</span>
            <div className="flex-1">
              <p className="text-[9px] font-medium text-[#1D1D1F]">{doc.name}</p>
              <p className="text-[8px] text-[#86868B]">{doc.type} · {doc.size}</p>
            </div>
            <span className="text-[8px] text-[#86868B]">{doc.date}</span>
            <Download className="w-3 h-3 text-gray-400" />
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function BranchesScreen() {
  const branches = [
    { name: 'Main Branch', city: 'Riyadh', staff: 18, revenue: '142,500', status: 'Active' },
    { name: 'Branch 2', city: 'Jeddah', staff: 12, revenue: '98,200', status: 'Active' },
    { name: 'Branch 3', city: 'Dammam', staff: 8, revenue: '45,800', status: 'Active' },
    { name: 'Branch 4', city: 'Dubai (UAE)', staff: 9, revenue: '67,300', status: 'Active' },
  ];
  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold text-[#1D1D1F]">4 Branches · 2 Countries</span>
        <motion.div className="flex items-center gap-1 px-2.5 py-1 bg-[#1D1D1F] rounded-md"
          initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.3, type: 'spring' }}>
          <Plus className="w-3 h-3 text-white" />
          <span className="text-[9px] font-medium text-white">Add Branch</span>
        </motion.div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {branches.map((b, i) => (
          <motion.div key={b.name}
            className="bg-white rounded-lg border border-gray-100 p-3 hover:shadow-md transition-shadow"
            initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 + i * 0.1 }}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center">
                  <Building2 className="w-4 h-4 text-info" />
                </div>
                <div>
                  <p className="text-[10px] font-semibold text-[#1D1D1F]">{b.name}</p>
                  <p className="text-[8px] text-[#86868B]">{b.city}</p>
                </div>
              </div>
              <span className="text-[7px] bg-success/10 text-success px-1.5 py-0.5 rounded-full font-medium">{b.status}</span>
            </div>
            <div className="flex items-center gap-4">
              <div>
                <p className="text-[12px] font-bold text-[#1D1D1F]">{cur()} {b.revenue}</p>
                <p className="text-[7px] text-[#86868B]">Monthly Revenue</p>
              </div>
              <div>
                <p className="text-[12px] font-bold text-[#1D1D1F]">{b.staff}</p>
                <p className="text-[7px] text-[#86868B]">Staff</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function AlertsScreen() {
  const alerts = [
    { severity: 'critical', title: 'Low Stock: Coffee Beans', desc: 'Only 2 kg remaining. Minimum is 10 kg.', action: 'Order Now', icon: Package, color: 'bg-error' },
    { severity: 'critical', title: 'Low Stock: Milk', desc: 'Only 5 liters remaining. Minimum is 20 L.', action: 'Order Now', icon: Package, color: 'bg-error' },
    { severity: 'warning', title: 'Payroll Deadline', desc: 'Payroll processing due in 3 days.', action: 'Review', icon: Clock, color: 'bg-warning' },
    { severity: 'warning', title: 'License Expiry', desc: 'Business license expires in 30 days.', action: 'Renew', icon: FileText, color: 'bg-warning' },
    { severity: 'info', title: 'Maintenance Due', desc: 'Espresso Machine #2 scheduled maintenance.', action: 'Schedule', icon: AlertTriangle, color: 'bg-info' },
  ];
  return (
    <div className="p-4 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
        {[
          { label: 'Critical', count: 2, color: 'from-red-500 to-rose-500', textColor: 'text-error' },
          { label: 'Warnings', count: 2, color: 'from-amber-500 to-orange-500', textColor: 'text-warning' },
          { label: 'Info', count: 1, color: 'from-blue-500 to-indigo-500', textColor: 'text-info' },
        ].map((s, i) => (
          <motion.div key={s.label} className="bg-white rounded-lg border border-gray-100 p-3 text-center"
            initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 + i * 0.1 }}>
            <p className={`text-[20px] font-bold ${s.textColor}`} style={heading}>{s.count}</p>
            <p className="text-[9px] text-[#86868B]">{s.label}</p>
          </motion.div>
        ))}
      </div>
      <div className="space-y-2">
        {alerts.map((a, i) => (
          <motion.div key={i}
            className={`flex items-start gap-3 p-3 rounded-lg border ${
              a.severity === 'critical' ? 'bg-error/10 border-error/30' :
              a.severity === 'warning' ? 'bg-warning/10 border-warning/30' : 'bg-info/10 border-info/30'
            }`}
            initial={{ opacity: 0, x: -15 }} animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 + i * 0.08 }}>
            <div className={`w-7 h-7 rounded-lg ${a.color} flex items-center justify-center shrink-0`}>
              <a.icon className="w-3.5 h-3.5 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-[10px] font-semibold text-[#1D1D1F]">{a.title}</p>
              <p className="text-[9px] text-[#86868B]">{a.desc}</p>
            </div>
            <motion.button className="text-[8px] font-semibold text-white bg-[#1D1D1F] px-2 py-1 rounded-md shrink-0"
              whileHover={{ scale: 1.05 }}>
              {a.action}
            </motion.button>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function ExpensesScreen() {
  return (
    <div className="p-4 space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        <KpiCard label="This Month" value="SR 42,300" change="+8%" positive={false} icon={Receipt} color="from-red-500 to-rose-500" delay={0.1} />
        <KpiCard label="Last Month" value="SR 39,100" icon={Calendar} color="from-gray-500 to-gray-600" delay={0.2} />
        <KpiCard label="Avg Daily" value="SR 1,410" icon={Clock} color="from-blue-500 to-indigo-500" delay={0.3} />
        <KpiCard label="Budget Left" value="SR 7,700" change="84%" positive icon={DollarSign} color="from-emerald-500 to-teal-500" delay={0.4} />
      </div>
      <div className="bg-white rounded-lg border border-gray-100 p-3">
        <p className="text-[10px] font-semibold text-[#1D1D1F] mb-2">Expense Breakdown</p>
        <div className="grid grid-cols-2 gap-x-6 gap-y-2">
          {[
            { cat: 'Salaries', amt: '18,500', pct: 44, color: 'from-blue-400 to-blue-500' },
            { cat: 'Rent', amt: '8,000', pct: 19, color: 'from-violet-400 to-violet-500' },
            { cat: 'Supplies', amt: '6,200', pct: 15, color: 'from-orange-400 to-orange-500' },
            { cat: 'Utilities', amt: '3,800', pct: 9, color: 'from-emerald-400 to-emerald-500' },
            { cat: 'Marketing', amt: '3,200', pct: 8, color: 'from-pink-400 to-pink-500' },
            { cat: 'Other', amt: '2,600', pct: 6, color: 'from-gray-400 to-gray-500' },
          ].map((e, i) => (
            <motion.div key={e.cat} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 + i * 0.08 }}>
              <div className="flex justify-between text-[8px] mb-0.5">
                <span className="text-[#86868B]">{e.cat}</span>
                <span className="font-medium text-[#1D1D1F]">{cur()} {e.amt}</span>
              </div>
              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <motion.div className={`h-full rounded-full bg-gradient-to-r ${e.color}`}
                  initial={{ width: 0 }} animate={{ width: `${e.pct}%` }}
                  transition={{ duration: 0.5, delay: 0.5 + i * 0.08 }} />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ProfitLossScreen() {
  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold text-[#1D1D1F]">Profit & Loss Statement</span>
        <div className="flex items-center gap-1.5">
          <span className="text-[8px] bg-gray-100 px-2 py-0.5 rounded-md text-[#86868B]">Feb 2026</span>
          <Download className="w-3.5 h-3.5 text-gray-400" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <motion.div className="bg-success/10 rounded-lg p-3 border border-success/30"
          initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
          <p className="text-[9px] font-medium text-success mb-1">Total Revenue</p>
          <p className="text-[20px] font-bold text-success" style={heading}>{cur()} 245,800</p>
        </motion.div>
        <motion.div className="bg-error/10 rounded-lg p-3 border border-error/30"
          initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }}>
          <p className="text-[9px] font-medium text-error mb-1">Total Expenses</p>
          <p className="text-[20px] font-bold text-error" style={heading}>{cur()} 142,300</p>
        </motion.div>
      </div>
      <motion.div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-info/30 text-center"
        initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
        <p className="text-[9px] font-medium text-info mb-1">Net Profit</p>
        <p className="text-[28px] font-bold text-info" style={heading}>{cur()} 103,500</p>
        <p className="text-[9px] text-info mt-0.5 flex items-center justify-center gap-1">
          <ArrowUpRight className="w-3 h-3" /> 34% higher than last month
        </p>
      </motion.div>
      <div className="bg-white rounded-lg border border-gray-100 p-3">
        <p className="text-[10px] font-semibold text-[#1D1D1F] mb-2">Monthly P&L Trend</p>
        <div className="flex items-end gap-2 h-16">
          {[
            { rev: 72, exp: 45 }, { rev: 68, exp: 42 }, { rev: 80, exp: 48 },
            { rev: 85, exp: 50 }, { rev: 78, exp: 44 }, { rev: 92, exp: 52 },
          ].map((m, i) => (
            <div key={i} className="flex-1 flex gap-0.5">
              <motion.div className="flex-1 bg-gradient-to-t from-emerald-400 to-emerald-300 rounded-sm"
                initial={{ height: 0 }} animate={{ height: `${m.rev}%` }}
                transition={{ duration: 0.5, delay: 0.5 + i * 0.08 }} />
              <motion.div className="flex-1 bg-gradient-to-t from-red-300 to-red-200 rounded-sm"
                initial={{ height: 0 }} animate={{ height: `${m.exp}%` }}
                transition={{ duration: 0.5, delay: 0.55 + i * 0.08 }} />
            </div>
          ))}
        </div>
        <div className="flex justify-between mt-1">
          {['Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb'].map(m => (
            <span key={m} className="text-[7px] text-[#86868B] flex-1 text-center">{m}</span>
          ))}
        </div>
        <div className="flex items-center justify-center gap-4 mt-2">
          <span className="flex items-center gap-1 text-[7px] text-[#86868B]"><div className="w-2 h-2 rounded-sm bg-emerald-400" /> Revenue</span>
          <span className="flex items-center gap-1 text-[7px] text-[#86868B]"><div className="w-2 h-2 rounded-sm bg-red-300" /> Expenses</span>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   TOUR STEPS CONFIG
   ═══════════════════════════════════════════ */
const TOUR_STEPS = [
  {
    id: 'dashboard',
    title: 'Dashboard',
    subtitle: 'Your Business at a Glance',
    desc: 'See real-time KPIs, revenue charts, top products, and alerts — all in one view. Every metric updates live across all your branches.',
    icon: BarChart3,
    color: 'from-orange-500 to-amber-500',
    Screen: DashboardScreen,
    tips: ['Real-time revenue tracking', 'Top-selling products', 'Multi-branch overview'],
  },
  {
    id: 'pos',
    title: 'Point of Sale',
    subtitle: 'Fast, Touch-Friendly Ordering',
    desc: 'Process orders in seconds with our industry-adaptive POS. Works for restaurants, retail, salons, and more — with barcode scanning, kitchen display, and split payments.',
    icon: Monitor,
    color: 'from-blue-500 to-indigo-600',
    Screen: POSScreen,
    tips: ['Quick product search', 'Multiple payment methods', 'Auto VAT calculation'],
  },
  {
    id: 'hr',
    title: 'HR & Payroll',
    subtitle: 'Complete Employee Management',
    desc: 'Manage the entire employee lifecycle — hiring, contracts, attendance, leave, and country-aware payroll for 31+ countries.',
    icon: Users,
    color: 'from-emerald-500 to-teal-600',
    Screen: HRScreen,
    tips: ['31+ country labor laws', 'Leave management', 'Automated payroll'],
  },
  {
    id: 'accounting',
    title: 'Accounting',
    subtitle: 'Numbers That Make Sense',
    desc: 'Full double-entry accounting with automated journal entries, bank reconciliation, and real-time financial dashboards.',
    icon: Calculator,
    color: 'from-violet-500 to-purple-600',
    Screen: AccountingScreen,
    tips: ['Chart of accounts', 'Auto journal entries', 'Bank reconciliation'],
  },
  {
    id: 'analytics',
    title: 'Analytics',
    subtitle: 'AI-Powered Insights',
    desc: 'Deep analytics with AI sales forecasting, category breakdowns, trend analysis, and predictive insights to grow your business.',
    icon: TrendingUp,
    color: 'from-cyan-500 to-blue-600',
    Screen: AnalyticsScreen,
    tips: ['AI sales forecast', 'Category breakdown', 'Trend analysis'],
  },
  {
    id: 'notifications',
    title: 'Notifications',
    subtitle: 'Never Miss an Alert',
    desc: 'Unified notification center aggregating leave approvals, stock alerts, payment updates, system events, and more — all prioritized by urgency.',
    icon: Bell,
    color: 'from-red-500 to-rose-600',
    Screen: NotificationsScreen,
    tips: ['Priority-based alerts', 'Real-time updates', 'One-click actions'],
  },
  {
    id: 'documents',
    title: 'Documents',
    subtitle: 'Organized & Accessible',
    desc: 'Store and manage all company documents — contracts, certificates, invoices, and policies. Quick search, download, and share with your team.',
    icon: FileText,
    color: 'from-indigo-500 to-blue-600',
    Screen: DocumentsScreen,
    tips: ['Secure storage', 'Quick search', 'Easy sharing'],
  },
  {
    id: 'branches',
    title: 'Branches',
    subtitle: 'Multi-Location Control',
    desc: 'Manage branches across cities and countries from a single dashboard. Compare performance, transfer stock, and sync data in real-time.',
    icon: Building2,
    color: 'from-amber-500 to-orange-600',
    Screen: BranchesScreen,
    tips: ['Multi-country support', 'Performance comparison', 'Real-time sync'],
  },
  {
    id: 'alerts',
    title: 'Alerts & Fixes',
    subtitle: 'Stay Ahead of Problems',
    desc: 'Proactive alerts for low stock, deadlines, maintenance schedules, and compliance. Take action directly from the alert panel.',
    icon: AlertTriangle,
    color: 'from-rose-500 to-red-600',
    Screen: AlertsScreen,
    tips: ['Stock level monitoring', 'Deadline tracking', 'One-click resolution'],
  },
  {
    id: 'expenses',
    title: 'Expenses',
    subtitle: 'Track Every Penny',
    desc: 'Comprehensive expense tracking with category breakdowns, budget monitoring, daily averages, and spending trends.',
    icon: Receipt,
    color: 'from-pink-500 to-rose-600',
    Screen: ExpensesScreen,
    tips: ['Category breakdown', 'Budget tracking', 'Spending trends'],
  },
  {
    id: 'pnl',
    title: 'Profit & Loss',
    subtitle: 'The Bottom Line',
    desc: 'Clear profit & loss statements with revenue vs expenses comparison, monthly trends, and growth indicators.',
    icon: DollarSign,
    color: 'from-emerald-600 to-green-600',
    Screen: ProfitLossScreen,
    tips: ['Revenue vs Expenses', 'Monthly trends', 'Growth tracking'],
  },
];

/* ═══════════════════════════════════════════
   SHARE DEMO DROPDOWN
   ═══════════════════════════════════════════ */
function ShareDemoDropdown() {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);
  const shareUrl = `${window.location.origin}?demo=true`;
  const shareText = 'Check out Smarto — a full ERP platform for any business. Take the interactive demo tour:';

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const input = document.createElement('input');
      input.value = shareUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`, '_blank');
  };

  const shareEmail = () => {
    window.open(`mailto:?subject=${encodeURIComponent('Try Smarto — Interactive Demo')}&body=${encodeURIComponent(shareText + '\n\n' + shareUrl)}`, '_blank');
  };

  const shareX = () => {
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, '_blank');
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(o => !o)}
        className="w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
        data-testid="tour-share-btn">
        <Share2 className="w-3.5 h-3.5 text-[#86868B]" />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            className="absolute right-0 top-9 w-56 bg-white rounded-xl border border-gray-100 shadow-[0_12px_40px_rgb(0,0,0,0.12)] overflow-hidden z-50"
            initial={{ opacity: 0, y: -8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            data-testid="share-dropdown">
            <div className="px-3 py-2.5 border-b border-gray-50">
              <p className="text-[11px] font-semibold text-[#1D1D1F]" style={heading}>Share This Demo</p>
              <p className="text-[9px] text-[#86868B] mt-0.5">Let others experience Smarto</p>
            </div>
            <div className="p-1.5 space-y-0.5">
              <button onClick={copyLink}
                className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-gray-50 transition-colors text-left"
                data-testid="share-copy-link">
                {copied ? (
                  <div className="w-7 h-7 rounded-lg bg-success/20 flex items-center justify-center">
                    <Check className="w-3.5 h-3.5 text-success" />
                  </div>
                ) : (
                  <div className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center">
                    <Link2 className="w-3.5 h-3.5 text-[#86868B]" />
                  </div>
                )}
                <div>
                  <p className="text-[11px] font-medium text-[#1D1D1F]">{copied ? 'Link Copied!' : 'Copy Link'}</p>
                  <p className="text-[8px] text-[#86868B]">{copied ? 'Share it anywhere' : 'Copy demo URL to clipboard'}</p>
                </div>
              </button>
              <button onClick={shareWhatsApp}
                className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-gray-50 transition-colors text-left"
                data-testid="share-whatsapp">
                <div className="w-7 h-7 rounded-lg bg-success flex items-center justify-center">
                  <MessageCircle className="w-3.5 h-3.5 text-white" />
                </div>
                <div>
                  <p className="text-[11px] font-medium text-[#1D1D1F]">WhatsApp</p>
                  <p className="text-[8px] text-[#86868B]">Share via WhatsApp</p>
                </div>
              </button>
              <button onClick={shareEmail}
                className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-gray-50 transition-colors text-left"
                data-testid="share-email">
                <div className="w-7 h-7 rounded-lg bg-info flex items-center justify-center">
                  <Mail className="w-3.5 h-3.5 text-white" />
                </div>
                <div>
                  <p className="text-[11px] font-medium text-[#1D1D1F]">Email</p>
                  <p className="text-[8px] text-[#86868B]">Send via email</p>
                </div>
              </button>
              <button onClick={shareX}
                className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-gray-50 transition-colors text-left"
                data-testid="share-x-twitter">
                <div className="w-7 h-7 rounded-lg bg-[#1D1D1F] flex items-center justify-center">
                  <span className="text-[10px] font-bold text-white">X</span>
                </div>
                <div>
                  <p className="text-[11px] font-medium text-[#1D1D1F]">X (Twitter)</p>
                  <p className="text-[8px] text-[#86868B]">Post on X</p>
                </div>
              </button>
            </div>
            <div className="px-3 py-2 border-t border-gray-50 bg-gray-50/50">
              <div className="flex items-center gap-1.5 bg-white rounded-md px-2 py-1.5 border border-gray-100">
                <Link2 className="w-3 h-3 text-[#86868B] shrink-0" />
                <span className="text-[9px] text-[#86868B] truncate flex-1">{shareUrl}</span>
                <button onClick={copyLink} className="text-[9px] font-semibold text-orange-500 hover:text-orange-600 shrink-0" data-testid="share-copy-mini">
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ═══════════════════════════════════════════
   MAIN PRODUCT TOUR MODAL
   ═══════════════════════════════════════════ */
export default function ProductTourModal({ isOpen, onClose }) {
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [activeSteps, setActiveSteps] = useState(TOUR_STEPS);
  const sessionRef = useRef(null);
  const stepEnteredAt = useRef(Date.now());
  const variantRef = useRef(null);

  const current = activeSteps[step];

  // Analytics tracking helper (fire-and-forget)
  const track = useCallback((event_type, extra = {}) => {
    if (!sessionRef.current) return;
    const payload = {
      event_type,
      session_id: sessionRef.current,
      source: sessionRef.source || 'direct',
      variant: variantRef.current || null,
      ...extra,
    };
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/demo-analytics/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).catch(() => {});
  }, []);

  // Track time spent when leaving a step
  const trackStepTime = useCallback((stepIdx) => {
    const elapsed = Math.round((Date.now() - stepEnteredAt.current) / 1000);
    if (elapsed > 0 && elapsed < 600) {
      track('step_time', {
        step_id: activeSteps[stepIdx]?.id,
        step_index: stepIdx,
        time_spent: elapsed,
      });
    }
    stepEnteredAt.current = Date.now();
  }, [track, activeSteps]);

  const goNext = useCallback(() => {
    if (step < activeSteps.length - 1) {
      trackStepTime(step);
      setDirection(1);
      setStep(s => s + 1);
    }
  }, [step, trackStepTime, activeSteps.length]);

  const goPrev = useCallback(() => {
    if (step > 0) {
      trackStepTime(step);
      setDirection(-1);
      setStep(s => s - 1);
    }
  }, [step, trackStepTime]);

  const goToStep = useCallback((i) => {
    trackStepTime(step);
    setDirection(i > step ? 1 : -1);
    setStep(i);
  }, [step, trackStepTime]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ') { e.preventDefault(); goNext(); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); goPrev(); }
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, goNext, goPrev, onClose]);

  // Track tour open + reset step + fetch A/B variant
  useEffect(() => {
    if (isOpen) {
      setStep(0);
      const sid = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      sessionRef.current = sid;
      // Store demo session_id for lead engagement tracking
      try { sessionStorage.setItem('smarto_demo_session', sid); } catch {}
      const wasShared = new URLSearchParams(window.location.search).get('demo') === 'true' ||
        document.referrer.includes('demo=true');
      sessionRef.source = wasShared ? 'shared_link' : 'direct';

      // Fetch variant assignment
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/demo-analytics/ab/assign`)
        .then(r => r.json())
        .then(data => {
          variantRef.current = data.variant_name || 'Control';

          // Build steps based on variant config
          const stepMap = {};
          TOUR_STEPS.forEach(s => { stepMap[s.id] = s; });
          const order = data.step_order || TOUR_STEPS.map(s => s.id);
          const descs = data.descriptions || {};

          const reorderedSteps = order
            .filter(id => stepMap[id])
            .map(id => {
              const base = { ...stepMap[id] };
              if (descs[id]) {
                if (descs[id].subtitle) base.subtitle = descs[id].subtitle;
                if (descs[id].desc) base.desc = descs[id].desc;
                if (descs[id].tips) base.tips = descs[id].tips;
              }
              return base;
            });

          setActiveSteps(reorderedSteps.length > 0 ? reorderedSteps : TOUR_STEPS);
          track('tour_open');
          track('step_view', { step_id: (reorderedSteps[0] || TOUR_STEPS[0]).id, step_index: 0 });
        })
        .catch(() => {
          variantRef.current = 'Control';
          setActiveSteps(TOUR_STEPS);
          track('tour_open');
          track('step_view', { step_id: TOUR_STEPS[0].id, step_index: 0 });
        });
    }
  }, [isOpen, track]);

  // Track step changes
  useEffect(() => {
    if (isOpen && step > 0 && sessionRef.current) {
      track('step_view', { step_id: activeSteps[step]?.id, step_index: step });
    }
  }, [step, isOpen, track, activeSteps]);

  const handleClose = useCallback(() => {
    trackStepTime(step);
    if (step < activeSteps.length - 1) {
      track('tour_close', { step_id: activeSteps[step]?.id, step_index: step });
    }
    onClose();
  }, [step, onClose, track, trackStepTime, activeSteps]);

  const handleFinish = useCallback(() => {
    trackStepTime(step);
    track('tour_complete', { step_id: activeSteps[step]?.id, step_index: step });
    onClose();
  }, [step, onClose, track, trackStepTime, activeSteps]);

  if (!isOpen) return null;

  const variants = {
    enter: (d) => ({ x: d > 0 ? 60 : -60, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (d) => ({ x: d > 0 ? -60 : 60, opacity: 0 }),
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid="product-tour-modal">
          {/* Backdrop */}
          <motion.div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} />

          {/* Modal */}
          <motion.div
            className="relative w-full max-w-5xl bg-[#F5F5F7] rounded-2xl shadow-2xl overflow-hidden flex flex-col"
            style={{ maxHeight: '85vh' }}
            initial={{ scale: 0.92, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.92, opacity: 0, y: 20 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}>

            {/* Header */}
            <div className="flex items-center justify-between px-6 py-3 bg-white border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-xl bg-gradient-to-br ${current.color} flex items-center justify-center shadow-sm`}>
                  <current.icon className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h2 className="text-[15px] font-semibold text-[#1D1D1F]" style={heading}>{current.title}</h2>
                  <p className="text-[11px] text-[#86868B]">{current.subtitle}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-[11px] text-[#86868B] font-medium">{step + 1} / {activeSteps.length}</span>
                <ShareDemoDropdown />
                <button onClick={handleClose}
                  className="w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                  data-testid="tour-close-btn">
                  <X className="w-4 h-4 text-[#86868B]" />
                </button>
              </div>
            </div>

            {/* Step Navigation Dots */}
            <div className="flex items-center justify-center gap-1 py-2 bg-white border-b border-gray-50">
              {activeSteps.map((s, i) => (
                <button key={s.id} onClick={() => goToStep(i)}
                  className={`group relative flex items-center gap-1 px-2 py-1 rounded-full text-[9px] font-medium transition-all ${
                    i === step ? 'bg-[#1D1D1F] text-white' : 'text-[#86868B] hover:bg-gray-100'
                  }`}
                  data-testid={`tour-dot-${s.id}`}>
                  <s.icon className="w-2.5 h-2.5" />
                  <span className="hidden lg:inline">{s.title}</span>
                </button>
              ))}
            </div>

            {/* Content Area */}
            <div className="flex-1 flex overflow-hidden" style={{ minHeight: 380 }}>
              {/* Description Panel */}
              <div className="w-60 bg-white p-5 flex flex-col justify-between border-r border-gray-100 shrink-0">
                <div>
                  <AnimatePresence mode="wait" custom={direction}>
                    <motion.div key={step}
                      custom={direction}
                      variants={variants}
                      initial="enter" animate="center" exit="exit"
                      transition={{ duration: 0.25 }}>
                      <p className="text-[13px] text-[#86868B] leading-relaxed">{current.desc}</p>
                      <div className="mt-5 space-y-2">
                        {current.tips.map((tip, i) => (
                          <motion.div key={tip}
                            className="flex items-center gap-2"
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.2 + i * 0.1 }}>
                            <Check className="w-3.5 h-3.5 text-success shrink-0" />
                            <span className="text-[11px] text-[#1D1D1F] font-medium">{tip}</span>
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  </AnimatePresence>
                </div>
                {/* Navigation */}
                <div className="flex items-center gap-2 mt-4">
                  <button onClick={goPrev} disabled={step === 0}
                    className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-30 flex items-center justify-center transition-all"
                    data-testid="tour-prev-btn">
                    <ChevronLeft className="w-4 h-4 text-[#1D1D1F]" />
                  </button>
                  <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-gradient-to-r from-orange-500 to-amber-500 rounded-full"
                      animate={{ width: `${((step + 1) / activeSteps.length) * 100}%` }}
                      transition={{ duration: 0.3 }}
                    />
                  </div>
                  <button onClick={step === activeSteps.length - 1 ? handleFinish : goNext}
                    className={`h-8 rounded-full flex items-center justify-center transition-all px-3 gap-1 text-[11px] font-semibold ${
                      step === activeSteps.length - 1
                        ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white'
                        : 'bg-[#1D1D1F] hover:bg-black text-white'
                    }`}
                    data-testid="tour-next-btn">
                    {step === activeSteps.length - 1 ? 'Get Started' : 'Next'}
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              {/* Mock App Screen */}
              <div className="flex-1 flex overflow-hidden">
                <MockSidebar activeStep={step} />
                <div className="flex-1 bg-[#FAFAFA] flex flex-col overflow-hidden">
                  <MockTopBar title={current.title} />
                  <div className="flex-1 overflow-y-auto">
                    <AnimatePresence mode="wait" custom={direction}>
                      <motion.div key={step}
                        custom={direction}
                        variants={variants}
                        initial="enter" animate="center" exit="exit"
                        transition={{ duration: 0.3 }}>
                        <current.Screen />
                      </motion.div>
                    </AnimatePresence>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
